<template>
  <v-card :loading="numbersLoading" class="align-stretch" height="100%">
    <v-card-title>
      Number Transfers
      <template v-if="!numbersLoading">
        <BeehiveIcon :link="articleLinks.numbersVoip" />

        <v-spacer></v-spacer>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="secondary" class="ml-2" x-small @click="openTransferNumber()">
              <v-icon small>transform</v-icon>
            </v-btn>
          </template>
          <span>Transfer Number</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" color="secondary" class="ml-2" x-small :href="queueUrl" target="_blank">
              <v-icon small>{{queueIcon}}</v-icon>
            </v-btn>
          </template>
          <span>Porting Queue</span>
        </v-tooltip>
      </template>
    </v-card-title>

    <template v-if="!numbersLoading">
      <v-card-text>
        <v-data-table :headers="headers" :items="getNumberRanges" disable-filtering disable-pagination disable-sort hide-default-footer>
          <template v-slot:item.porting_range_start="{ item }">
            <span class="text-no-wrap body--text">{{ numberTransferRange(item) | customPlaceholder }}</span>
          </template>

          <template v-slot:item.port_kind="{ item }">
            <span class="body--text">{{ getKind(item.port_kind) }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <div class="d-flex justify-space-between align-center">
              <span class="body--text mr-2">{{ item.status | formatTableData }}</span>
            
              <div class="d-flex align-center no-wrap">
                <span class="mr-1">
                  <WaitForBroadbandIcon v-if="item.broadband_product_id" :item="item" />
                </span>
                <span class="mr-1">
                  <SlaFlagIcon v-if="item.warning_flag" :warningFlag="item.warning_flag" />
                </span>
                <span class="mr-1">
                  <AddressCheckStatus :showText="false" :status="item.address_check" />
                </span>
                <span class="mr-1">
                  <ConfirmedTransferDateStatusIcon v-if="item.confirmed_transfer_date" :confirmedTransferDate="item.confirmed_transfer_date" />
                </span>
              </div> 
            </div>
          </template>

          <template v-slot:item.porting_status="{ item }">
            <span class="body--text">{{ item.porting_status | customPlaceholder }}</span>
          </template>
          
          <template v-slot:item.actions="{ item }">
            <div class="d-flex no-flex">
              <template v-if="!isInStatus(['PORTED'], item)">
                <v-btn class="mr-2" color="error" x-small :disabled="getDeletePortingNumberLoading" :loading="deleteNumberLoaders[item.id]" @click="deletePortingNumberByID(item)">
                  <v-icon small>delete</v-icon>
                </v-btn>

                <v-btn class="mr-2" color="secondary" x-small :disabled="getCurrentNumberPortingLoading" :loading="editPortingLoaders[item.id]" @click="editNumberPorting(item)">
                  <v-icon small>edit</v-icon>
                </v-btn>
              </template>

              <template>
                <v-menu nudge-bottom="20">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn x-small color="secondary" v-bind="attrs" v-on="on">
                      <v-icon>{{ menuIcon }}</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item :disabled="disableCreateTransferOrder(item)">
                      <CreateTransferOrder :item="item" @refresh-numbers="refreshNumbers" />
                    </v-list-item>

                    <v-list-item :disabled="disableCloaStatus(item)">
                      <CloaStatus :item="item" />
                    </v-list-item>

                    <v-list-item :disabled="disableSendCloa(item)">
                      <SendCloa :item="item" />
                    </v-list-item>

                    <v-list-item :disabled="disableEmailPreview(item)" @click="openEmailPreviewDialog(item.id, 'mismatch')"> 
                      <span>SEND PORTING INFORMATION MISMATCH EMAIL</span>
                    </v-list-item>

                    <v-list-item :disabled="disableEmailPreview(item)" @click="openEmailPreviewDialog(item.id, 'cancellation')">
                      <span>SEND LCP CANCELLATION EMAIL</span>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </template>

    <TransferNumber :productId="getCurrentProduct.id" :open="transferNumberOpen" @closeDialog="transferNumberOpen=false;" :currentNumberPorting="getCurrentNumberPorting" @refresh-numbers="refreshNumbers" />
    <EmailPreviewNumbersDialog :dialogOpen="emailPreviewOpen" :numberTransferId="numberTransferId" :emailType="emailPreviewType" @closeDialog="emailPreviewOpen=false" />  
  </v-card>
</template>

<script>
  import TransferNumber from "./TransferNumber";
  import CreateTransferOrder from "./CreateTransferOrder";
  import SendCloa from "@/components/Products/Numbers/SendCloa";
  import CloaStatus from "@/components/Products/Numbers/CloaStatus";
  import AddressCheckStatus from "@/components/NumberTransfers/AddressCheckStatus";
  import ConfirmedTransferDateStatusIcon from "@/components/NumberTransfers/ConfirmedTransferDateStatusIcon";
  import SlaFlagIcon from '@/components/NumberTransfers/SlaFlagIcon';
  import WaitForBroadbandIcon from "@/components/NumberTransfers/WaitForBroadbandIcon";
  import BeehiveIcon from '@/components/Globals/BeehiveIcon';
  import EmailPreviewNumbersDialog from '@/components/Products/Numbers/EmailPreviewNumbersDialog';

  import { mapActions, mapGetters } from "vuex";
  import { articleLinks } from '@/helpers/links';
  import { mdiFormatListText, mdiDotsHorizontal } from '@mdi/js';

  export default {
    components: { 
      TransferNumber,
      CreateTransferOrder,
      SendCloa,CloaStatus,
      AddressCheckStatus,
      ConfirmedTransferDateStatusIcon,
      SlaFlagIcon,
      WaitForBroadbandIcon,
      BeehiveIcon,
      EmailPreviewNumbersDialog 
    },

    props: ['numbersLoading'],
    
    data: () => ({
      headers: [
        { text: 'Number Range',  value: 'porting_range_start' },
        { text: 'Port Kind', value: 'port_kind' },
        { text: 'Status', value: 'status' },
        { text: 'Automation', value: 'porting_status', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      numberTransferId: null,
      transferNumberOpen: false,
      emailPreviewOpen: false,
      emailPreviewType: '',
      queue_url: process.env.VUE_APP_QUEUE_URL || `https://queues-bonline-testing.appspot.com/`,
      queueIcon: mdiFormatListText,
      menuIcon: mdiDotsHorizontal,
      articleLinks,
      deleteNumberLoaders: {},
      editPortingLoaders: {},
      portKindMap: {
        MANUAL_GEOGRAPHIC: 'Manual Geographic',
        GEOGRAPHIC: 'Geographic',
        NON_GEOGRAPHIC: 'Non-Geographic',
        IPEX: 'IPEX',
        RESELLER: 'Reseller'
      }
    }),

    filters: {
      formatTableData(val) {
        if(!val) return '-';
        const split = val.split('_').join(" ").toLowerCase();
        const caps = split.charAt(0).toUpperCase() + split.slice(1);
        return caps;
      }
    },

    computed: {
      ...mapGetters('numbers', ['getCurrentNumberPorting']),
      ...mapGetters('numbers', ['getDeletePortingNumberLoading', 'getCurrentNumberPortingLoading', 'getDeletePortingNumberError', 'getNumberRanges']),
      ...mapGetters('products', ['getAllProductNumbers', 'getCurrentProduct']),
      ...mapGetters('globals', ['hasUserGotRoles']),

      queueUrl() {
        return `${this.queue_url}voip-porting/actions/?product_id=${this.getCurrentProduct.id}`;
      },

      numberTransferRange() {
        return (item) => {
          if (item.porting_range_length <= 1) {
            return `${item.porting_range_start} - ${item.porting_range_start}`;
          }
          return `${item.porting_range_start} - 0${Number(item.porting_range_start) + Number(item.porting_range_length) - 1}`;
        }
      },

      getKind() {
        return (item) => {
          if(!item) return '-';
          if(!this.portKindMap[item]) return item;
          return this.portKindMap[item];
        }
      },
    },

    methods: {
      ...mapActions('numbers', ['getNumberPorting', 'deletePortingNumber']),
      ...mapActions('products', ['fetchProductNumbers']),

      isInStatus(status, item) {
        return status.includes(item.status);
      },

      async deletePortingNumberByID(item) {
        if(confirm('Are you sure you wish to delete this number?')){
          this.$set(this.deleteNumberLoaders, item.id, true);
          await this.deletePortingNumber({id: item.id, numberTransferRange: this.numberTransferRange(item)});

          if (!this.getDeletePortingNumberError) {
            await this.fetchProductNumbers(this.getCurrentProduct.id);
            this.$emit('newNumberRangeAdded');
          }

          this.$delete(this.deleteNumberLoaders, item.id);
        }
      },

      openTransferNumber() {
        this.transferNumberOpen=true;
      },

      async editNumberPorting (item) {
        this.$set(this.editPortingLoaders, item.id, true);
        await this.getNumberPorting({numberRangeId: item.id});
        this.transferNumberOpen = true;
        this.$delete(this.editPortingLoaders, item.id);
      },

      refreshNumbers() {
        this.$emit('newNumberRangeAdded');
      },

      disableCreateTransferOrder(item) {
        if (item.port_kind !== 'NON_GEOGRAPHIC') {
          return item.porting_status !== '' && item.porting_status !== null && ['cancelled','error','failed'].indexOf(item.porting_status) === -1;
        }
        return true;
      },

      disableCloaStatus(item) {
        if (this.hasUserGotRoles(['number-transfer-manager'])) {
          return item.cloa_sent === null;
        }
        return true;
      },

      disableSendCloa(item) {
        if (this.hasUserGotRoles(['number-transfer-manager', 'can-send-voip-cloa'])) {
          return item.cloa_sent !== null;
        }
        return true;
      },

      disableEmailPreview(item) {
        return item.port_kind === 'GEOGRAPHIC';
      },

      openEmailPreviewDialog(id, emailType) {
        this.emailPreviewOpen = true;
        this.emailPreviewType = emailType;
        this.numberTransferId = id;
      },
    },
  };
</script>

<style lang="scss" scoped></style>
